import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { KeyRound, Loader2, RefreshCw, Lock, Target, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";

const fetchKeywords = () => api.get("/admin/keywords").then((r) => r.data);
const rebuildKeywords = () => api.post("/admin/keywords/rebuild").then((r) => r.data);

const STATUS_META = {
  gap: { label: "Gap", cls: "bg-launch/10 text-launch border-launch/20", icon: Target },
  cannibalized: { label: "Cannibalized", cls: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle },
  covered: { label: "Covered", cls: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2 },
};

export default function KeywordIntelligence() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["keywords"], queryFn: fetchKeywords });
  const [filter, setFilter] = useState("all");
  const rebuild = useMutation({ mutationFn: rebuildKeywords, onSuccess: (d) => qc.setQueryData(["keywords"], d) });

  if (isLoading || !data) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const { keywords, summary } = data;
  const shown = filter === "all" ? keywords : keywords.filter((k) => k.status === filter);

  const cards = [
    { key: "all", label: "Total Keywords", value: summary.total, cls: "text-navy" },
    { key: "gap", label: "Content Gaps", value: summary.gaps, cls: "text-launch" },
    { key: "cannibalized", label: "Cannibalized", value: summary.cannibalized, cls: "text-amber-600" },
    { key: "covered", label: "Covered", value: summary.covered, cls: "text-green-600" },
  ];

  return (
    <div data-testid="keyword-intel-root">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2"><KeyRound className="h-6 w-6 text-launch" /> Keyword Intelligence</h1>
          <p className="text-slate-500 mt-1 max-w-3xl">Every keyword your site targets, ranked by an <strong>Opportunity Score (0–100)</strong> computed from real signals — keyword intent, coverage gaps, cannibalization, and internal linking.</p>
        </div>
        <button onClick={() => rebuild.mutate()} disabled={rebuild.isPending} data-testid="keyword-rebuild-btn" className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
          {rebuild.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Rebuild
        </button>
      </div>

      {/* Provider-ready notice */}
      <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 flex items-center gap-3" data-testid="keyword-provider-locked">
        <Lock className="h-5 w-5 text-slate-400" />
        <p className="text-xs text-slate-500">Search volume, keyword difficulty and current rank are locked. Connect <strong>Google Search Console</strong> or <strong>SerpApi</strong> to layer real volume &amp; rankings on top of these opportunity scores.</p>
      </div>

      {/* Summary cards */}
      <div className="mt-5 grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <button key={c.key} onClick={() => setFilter(c.key)} data-testid={`keyword-card-${c.key}`}
            className={`text-left bg-white border rounded-xl p-4 transition-colors ${filter === c.key ? "border-navy ring-1 ring-navy/20" : "border-slate-200 hover:border-slate-300"}`}>
            <p className="text-xs font-semibold text-slate-500">{c.label}</p>
            <p className={`mt-1 text-2xl font-extrabold ${c.cls}`}>{c.value}</p>
          </button>
        ))}
        <div className="bg-navy text-white rounded-xl p-4" data-testid="keyword-avg-score">
          <p className="text-xs font-semibold text-slate-300 flex items-center gap-1"><Sparkles className="h-3 w-3 text-launch" /> Avg Score</p>
          <p className="mt-1 text-2xl font-extrabold">{summary.avg_score}</p>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Keyword</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold w-48">Opportunity</th>
                <th className="text-left px-4 py-3 font-semibold">Target Page</th>
                <th className="text-left px-4 py-3 font-semibold text-slate-300">Volume</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((k) => {
                const meta = STATUS_META[k.status] || STATUS_META.covered;
                const Icon = meta.icon;
                return (
                  <tr key={k.normalized} className="border-t border-slate-100" data-testid={`keyword-row-${k.normalized}`}>
                    <td className="px-4 py-3 font-medium text-navy">{k.keyword}{k.cannibal_count > 1 && <span className="ml-2 text-xs text-amber-600">×{k.cannibal_count} pages</span>}</td>
                    <td className="px-4 py-3 text-slate-500">{k.type}</td>
                    <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 text-xs font-semibold border rounded-full px-2 py-0.5 ${meta.cls}`}><Icon className="h-3 w-3" /> {meta.label}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden max-w-[120px]"><div className="h-full bg-launch rounded-full" style={{ width: `${k.opportunity_score}%` }} /></div>
                        <span className="text-xs font-bold text-navy w-6">{k.opportunity_score}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">{k.source_url ? <a href={k.source_url} target="_blank" rel="noreferrer" className="text-cool hover:underline">{k.source_url}</a> : <span className="text-slate-400">— none —</span>}</td>
                    <td className="px-4 py-3 text-slate-300 select-none">—</td>
                  </tr>
                );
              })}
              {shown.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No keywords in this view.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
